import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Table, Loader2, AlertCircle, Info, Plus, Trash2, X } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = '/api/tarifas/comisiones-volumen';

const TRAMO_VACIO = () => ({ desde: '', hasta: '', comision_euros_cups: '' });

export default function ComisionesVolumenMatriz() {
  const queryClient = useQueryClient();
  const [esquemaSeleccionado, setEsquemaSeleccionado] = useState(null);
  const [vigenciaSeleccionada, setVigenciaSeleccionada] = useState(null);
  const [mostrarFormulario, setMostrarFormulario] = useState(false);
  const [nuevoEsquema, setNuevoEsquema] = useState('');
  const [nuevaVigencia, setNuevaVigencia] = useState('');
  const [tramos, setTramos] = useState([TRAMO_VACIO(), TRAMO_VACIO()]);

  const { data, isLoading, error } = useQuery({
    queryKey: ['comisiones-volumen-matriz'],
    queryFn: async () => {
      const response = await axios.get(`${API_URL}/matriz`);
      return response.data;
    },
  });

  const crearVigenciaMutation = useMutation({
    mutationFn: async (payload) => {
      const response = await axios.post(API_URL, payload);
      return response.data;
    },
    onSuccess: (data) => {
      toast.success(data.message || 'Vigencia creada correctamente');
      queryClient.invalidateQueries(['comisiones-volumen-matriz']);
      setMostrarFormulario(false);
      setNuevoEsquema('');
      setNuevaVigencia('');
      setTramos([TRAMO_VACIO(), TRAMO_VACIO()]);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Error creando la vigencia');
    },
  });

  const eliminarVigenciaMutation = useMutation({
    mutationFn: async ({ esquema, vigente_desde }) => {
      const response = await axios.delete(`${API_URL}/${esquema}/${vigente_desde}`);
      return response.data;
    },
    onSuccess: (data) => {
      toast.success(data.message || 'Vigencia eliminada');
      queryClient.invalidateQueries(['comisiones-volumen-matriz']);
      setVigenciaSeleccionada(null);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Error eliminando la vigencia');
    },
  });

  const esquemas = data?.esquemas || {};
  const nombresEsquemas = Object.keys(esquemas).sort();

  const esquemaActivo = esquemaSeleccionado || nombresEsquemas[0] || null;
  const vigenciasDelEsquema = esquemaActivo ? Object.keys(esquemas[esquemaActivo] || {}).sort().reverse() : [];
  const vigenciaActiva = vigenciaSeleccionada || vigenciasDelEsquema[0] || null;
  const tramosVisibles = esquemaActivo && vigenciaActiva ? esquemas[esquemaActivo][vigenciaActiva] || [] : [];

  const handleAddTramoRow = () => setTramos([...tramos, TRAMO_VACIO()]);
  const handleRemoveTramoRow = (idx) => setTramos(tramos.filter((_, i) => i !== idx));
  const handleTramoChange = (idx, campo, valor) => {
    const copia = [...tramos];
    copia[idx] = { ...copia[idx], [campo]: valor };
    setTramos(copia);
  };

  const handleSubmitVigencia = (e) => {
    e.preventDefault();

    const tramosPayload = tramos
      .filter((t) => t.desde !== '' && t.comision_euros_cups !== '')
      .map((t) => ({
        desde: parseInt(t.desde, 10),
        hasta: t.hasta === '' ? null : parseInt(t.hasta, 10),
        comision_euros_cups: parseFloat(t.comision_euros_cups)
      }));

    if (!nuevoEsquema.trim() || !nuevaVigencia || tramosPayload.length === 0) {
      toast.error('Completa el esquema, la fecha de vigencia y al menos un tramo');
      return;
    }

    crearVigenciaMutation.mutate({
      esquema: nuevoEsquema.trim().toUpperCase(),
      vigente_desde: nuevaVigencia,
      tramos: tramosPayload
    });
  };

  const handleEliminarVigencia = () => {
    if (!esquemaActivo || !vigenciaActiva) return;
    if (!window.confirm(`¿Eliminar la vigencia ${vigenciaActiva} de ${esquemaActivo}? Esta acción no se puede deshacer.`)) {
      return;
    }
    eliminarVigenciaMutation.mutate({ esquema: esquemaActivo, vigente_desde: vigenciaActiva });
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-primary animate-spin" />
          <span className="ml-3 text-gray-600">Cargando comisiones de volumen de CUPS...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center space-x-3 text-red-600">
          <AlertCircle className="w-6 h-6 flex-shrink-0" />
          <div>
            <p className="font-medium">Error al cargar comisiones de volumen de CUPS</p>
            <p className="text-sm text-red-500 mt-1">
              {error.response?.data?.error || error.message}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md">
      {/* Header */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center space-x-3">
            <Table className="w-6 h-6 text-primary" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                Comisiones por Volumen de CUPS
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                {data?.metadata?.total_esquemas || 0} esquema(s), {data?.metadata?.total_tramos || 0} tramos cargados
              </p>
            </div>
          </div>

          <button
            onClick={() => setMostrarFormulario(!mostrarFormulario)}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:opacity-90 transition-colors"
          >
            {mostrarFormulario ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
            {mostrarFormulario ? 'Cancelar' : 'Nueva vigencia'}
          </button>
        </div>

        {nombresEsquemas.length > 0 && (
          <div className="mt-4 flex items-center gap-3 flex-wrap">
            <select
              value={esquemaActivo || ''}
              onChange={(e) => {
                setEsquemaSeleccionado(e.target.value);
                setVigenciaSeleccionada(null);
              }}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-white"
            >
              {nombresEsquemas.map((esquema) => (
                <option key={esquema} value={esquema}>{esquema}</option>
              ))}
            </select>

            <select
              value={vigenciaActiva || ''}
              onChange={(e) => setVigenciaSeleccionada(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-white"
            >
              {vigenciasDelEsquema.map((vigencia) => (
                <option key={vigencia} value={vigencia}>Vigente desde {vigencia}</option>
              ))}
            </select>

            {vigenciaActiva && (
              <button
                onClick={handleEliminarVigencia}
                className="flex items-center gap-2 px-3 py-2 text-sm text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-colors"
              >
                <Trash2 className="w-4 h-4" />
                Eliminar vigencia
              </button>
            )}
          </div>
        )}

        <div className="mt-4 flex items-start space-x-3 p-4 bg-blue-50 rounded-lg">
          <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-medium text-blue-900">¿Cómo funciona este esquema?</p>
            <p className="text-blue-700 mt-1">
              Cada tramo indica el importe fijo (€/CUPS) que se paga cuando el nº de CUPS
              activados en el mes natural, agrupados por canal, cae dentro de ese rango. Un
              esquema puede tener varias vigencias: se aplica siempre la más reciente cuya
              fecha sea anterior o igual a la fecha de inicio de producto.
            </p>
          </div>
        </div>
      </div>

      {/* Formulario nueva vigencia */}
      {mostrarFormulario && (
        <form onSubmit={handleSubmitVigencia} className="p-6 border-b border-gray-200 bg-gray-50 space-y-4">
          <div className="flex gap-4 flex-wrap">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Esquema</label>
              <input
                type="text"
                value={nuevoEsquema}
                onChange={(e) => setNuevoEsquema(e.target.value)}
                placeholder="AUREUM"
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Vigente desde</label>
              <input
                type="date"
                value={nuevaVigencia}
                onChange={(e) => setNuevaVigencia(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">Tramos</label>
              <button
                type="button"
                onClick={handleAddTramoRow}
                className="text-sm text-primary hover:underline flex items-center gap-1"
              >
                <Plus className="w-3.5 h-3.5" /> Añadir tramo
              </button>
            </div>

            <div className="space-y-2">
              {tramos.map((tramo, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <input
                    type="number"
                    placeholder="Desde"
                    value={tramo.desde}
                    onChange={(e) => handleTramoChange(idx, 'desde', e.target.value)}
                    className="w-24 px-2 py-1.5 border border-gray-300 rounded text-sm"
                  />
                  <span className="text-gray-400">-</span>
                  <input
                    type="number"
                    placeholder="Hasta (vacío = sin límite)"
                    value={tramo.hasta}
                    onChange={(e) => handleTramoChange(idx, 'hasta', e.target.value)}
                    className="w-40 px-2 py-1.5 border border-gray-300 rounded text-sm"
                  />
                  <span className="text-gray-400">→</span>
                  <input
                    type="number"
                    step="0.01"
                    placeholder="€/CUPS"
                    value={tramo.comision_euros_cups}
                    onChange={(e) => handleTramoChange(idx, 'comision_euros_cups', e.target.value)}
                    className="w-32 px-2 py-1.5 border border-gray-300 rounded text-sm"
                  />
                  <button
                    type="button"
                    onClick={() => handleRemoveTramoRow(idx)}
                    className="text-gray-400 hover:text-red-600"
                    disabled={tramos.length <= 1}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <button
            type="submit"
            disabled={crearVigenciaMutation.isPending}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:opacity-90 transition-colors disabled:opacity-50"
          >
            {crearVigenciaMutation.isPending ? 'Guardando...' : 'Guardar vigencia'}
          </button>
        </form>
      )}

      {/* Tabla de tramos */}
      <div className="p-6 overflow-x-auto">
        {nombresEsquemas.length === 0 ? (
          <div className="flex items-center space-x-3 text-yellow-600">
            <AlertCircle className="w-6 h-6 flex-shrink-0" />
            <p>No hay esquemas de volumen de CUPS cargados todavía.</p>
          </div>
        ) : (
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-gray-100">
                <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-700">
                  Volumen CUPS (mes/canal)
                </th>
                <th className="border border-gray-300 px-4 py-3 text-right font-semibold text-gray-700">
                  Comisión €/CUPS
                </th>
              </tr>
            </thead>
            <tbody>
              {tramosVisibles
                .slice()
                .sort((a, b) => a.desde - b.desde)
                .map((tramo, idx) => (
                  <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="border border-gray-300 px-4 py-3 font-medium text-gray-900">
                      {tramo.desde} - {tramo.hasta !== null ? tramo.hasta : '∞'}
                    </td>
                    <td className="border border-gray-300 px-4 py-3 text-right font-mono text-gray-900">
                      {tramo.comision_euros_cups.toFixed(2)} €
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
